// src/data/m2/Unit4.js

export const M2_UNIT_4 = {
  id: "m2-u4",
  title: "Unit 4: Around Town",
  topic: "Places & Directions",
  iconType: "map",
  grammarImage: "/m2-u4-grammar.jpg", 
  grammarText: "Para ubicarte en la ciudad usamos **'There is/are'** y preposiciones como **'Next to'** o **'Opposite'**. 🇨🇦 Nota: En Canadá preferimos 'Washroom' y 'Centre'.",
  
  sets: [
    // ----------------------------------------------------------------------
    // SET 1: BASIC PLACES (Reconocimiento Visual y Definiciones)
    // Objetivo: Vocabulario esencial + Spelling Canadiense
    // ----------------------------------------------------------------------
    {
      id: "u20-set1",
      title: "Set 1: Essential Places",
      questions: [
        { 
          type: "mc", 
          q: "Lugar donde aterrizan los aviones ✈️:", 
          options: ["Airport", "Station", "Port"], 
          a: "Airport",
          tip: "Air (aire) + Port (puerto) = Airport."
        },
        { 
          type: "image_mc", 
          q: "¿Cuál es el 'Hospital'? 🚑", 
          options: ["/img/hospital.png", "/img/bank.png", "/img/school.png"], 
          a: "/img/hospital.png" // Asegúrate de tener estas imágenes o usa texto si no
        },
        { 
          type: "blank", 
          q: "Compramos comida en el ___ (supermercado).", 
          options: ["supermarket"], 
          a: "supermarket" 
        },
        { 
          type: "mc", 
          q: "Centro de la ciudad: City ___.", 
          options: ["Centre", "Center"], 
          a: "Centre",
          successTip: "🇨🇦 ¡Ojo! En Canadá y UK escribimos 'Centre' (re). En USA es 'Center' (er)."
        },
        { 
          type: "match", 
          q: "Conecta el lugar con su función:",
          pairs: [
            { left: "School", right: "📚 Learn (Aprender)" },
            { left: "Bank", right: "💰 Money (Dinero)" },
            { left: "Park", right: "🌳 Relax (Relajarse)" },
            { left: "Pharmacy", right: "💊 Medicine (Medicina)" }
          ],
          a: "completed"
        },
        { 
          type: "order", 
          q: "Ordena: ¿Dónde está el banco?", 
          words: ["Where", "is", "the", "bank?"], 
          a: "Where is the bank?" 
        },
        { 
          type: "input", 
          q: "Escribe 'Museo' en inglés:", 
          a: "Museum" 
        },
        { 
          type: "mc", 
          q: "Lugar pequeño con pocas casas:", 
          options: ["Village", "City", "Town"], 
          a: "Village" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 2: TRANSPORT & TRAVEL
    // Objetivo: Moverse por la ciudad. Introducción al "Metro".
    // ----------------------------------------------------------------------
    {
      id: "u20-set2",
      title: "Set 2: Transportation",
      questions: [
        { 
          type: "mc", 
          q: "Esperas el autobús urbano aquí:", 
          options: ["Bus stop", "Bus station", "Train station"], 
          a: "Bus stop",
          tip: "'Stop' es la parada pequeña en la calle. 'Station' es la terminal grande."
        },
        { 
          type: "match", 
          q: "Conecta el vehículo con su lugar:",
          pairs: [
            { left: "Train", right: "🚉 Station" },
            { left: "Plane", right: "✈️ Airport" },
            { left: "Bus", right: "🚏 Stop" },
            { left: "Ship", right: "⚓ Port" }
          ],
          a: "completed"
        },
        { 
          type: "input", 
          q: "El tren sale de la ___ (station).", 
          a: "station" 
        },
        { 
          type: "mc", 
          q: "En Montreal/Canadá, al tren subterráneo le llaman:", 
          options: ["Metro", "Subway", "Underground"], 
          a: "Metro",
          successTip: "🇨🇦 ¡Así es! En Montreal decimos 'Le Métro'. En Toronto es 'Subway'."
        },
        { 
          type: "order", 
          q: "Ordena: Voy al aeropuerto.", 
          words: ["I", "am", "going", "to", "the", "airport."], 
          a: "I am going to the airport." 
        },
        { 
          type: "mc", 
          q: "Viajas largas distancias desde aquí:", 
          options: ["Bus station", "Bus stop"], 
          a: "Bus station" 
        },
        { 
          type: "blank", 
          q: "Necesito un ___ (taxi) para ir al hotel.", 
          options: ["taxi"], 
          a: "taxi",
          successTip: "También puedes decir 'Cab'."
        },
        { 
          type: "order", 
          q: "Ordena: El tren llega tarde.", 
          words: ["The", "train", "is", "late."], 
          a: "The train is late." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 3: PREPOSITIONS OF PLACE (Logic & Spatial)
    // Objetivo: Next to, Opposite, Between, Behind.
    // ----------------------------------------------------------------------
    {
      id: "u20-set3",
      title: "Set 3: Where is it?",
      questions: [
        { 
          type: "match", 
          q: "Empareja la preposición con su lógica:",
          pairs: [
            { left: "A [B] C", right: "Between (En medio)" },
            { left: "A |Calle| B", right: "Opposite (En frente)" },
            { left: "A B", right: "Next to (Al lado)" },
            { left: "A (B está atrás)", right: "Behind (Detrás)" }
          ],
          a: "completed",
          tip: "'Opposite' implica que están cara a cara (face to face)."
        },
        { 
          type: "mc", 
          q: "Si algo está 'detrás' de ti, está:", 
          options: ["Behind", "In front of", "Next to"], 
          a: "Behind" 
        },
        { 
          type: "blank", 
          q: "El parque está ___ (cerca) de aquí.", 
          options: ["near"], 
          a: "near",
          successTip: "Lo contrario de 'Near' es 'Far' (lejos)."
        },
        { 
          type: "order", 
          q: "Ordena: La escuela está al lado del banco.", 
          words: ["The", "school", "is", "next", "to", "the", "bank."], 
          a: "The school is next to the bank." 
        },
        { 
          type: "mc", 
          q: "En la esquina = On the ___", 
          options: ["corner", "side", "border"], 
          a: "corner" 
        },
        { 
          type: "input", 
          q: "Escribe la preposición: 'Entre' A y B.", 
          a: "between" 
        },
        { 
          type: "mc", 
          q: "Diagrama: [Tienda] [Café]. El café está ___ la tienda.", 
          options: ["next to", "opposite", "between"], 
          a: "next to" 
        },
        { 
          type: "order", 
          q: "Ordena: Está enfrente del parque.", 
          words: ["It", "is", "opposite", "the", "park."], 
          a: "It is opposite the park." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 4: CANADIAN SURVIVAL & SHOPPING
    // Objetivo: Vocabulary Real (Washroom vs Restroom).
    // ----------------------------------------------------------------------
    {
      id: "u20-set4",
      title: "Set 4: Canadian Style 🇨🇦",
      questions: [
        { 
          type: "mc", 
          q: "Estás en un restaurante en Toronto. Quieres ir al baño. Preguntas por:", 
          options: ["The washroom", "The toilet", "The water closet"], 
          a: "The washroom",
          successTip: "🇨🇦 ¡Sí! En Canadá es muy común decir 'Washroom'. Es educado y estándar."
        },
        { 
          type: "match", 
          q: "Variedades de Inglés (US vs UK/Canadá):",
          pairs: [
            { left: "Store (US)", right: "Shop (UK/Can)" },
            { left: "Movie Theater (US)", right: "Cinema (UK/Can)" },
            { left: "Mall (US)", right: "Shopping Centre (Can)" }
          ],
          a: "completed"
        },
        { 
          type: "mc", 
          q: "Lugar para ver arte antiguo:", 
          options: ["Museum", "Library", "Theater"], 
          a: "Museum" 
        },
        { 
          type: "blank", 
          q: "Tomamos café en un ___.", 
          options: ["café"], 
          a: "café",
          tip: "Se escribe igual en español e inglés (a veces sin tilde: cafe)."
        },
        { 
          type: "input", 
          q: "Escribe la palabra canadiense para 'Baño' (W...):", 
          a: "washroom" 
        },
        { 
          type: "order", 
          q: "Ordena: ¿Dónde está el baño?", 
          words: ["Where", "is", "the", "washroom?"], 
          a: "Where is the washroom?" 
        },
        { 
          type: "mc", 
          q: "Vas a comprar ropa. Vas a una:", 
          options: ["Clothing store/shop", "Library", "Factory"], 
          a: "Clothing store/shop" 
        },
        { 
          type: "input", 
          q: "Lugar donde pides comida y te sirven: ___", 
          a: "restaurant" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 5: SERVICES & GOVERNMENT
    // Objetivo: Instituciones y servicios públicos.
    // ----------------------------------------------------------------------
    {
      id: "u20-set5",
      title: "Set 5: Public Services",
      questions: [
        { 
          type: "mc", 
          q: "Donde trabaja la policía:", 
          options: ["Police Station", "Fire Station", "Post Office"], 
          a: "Police Station" 
        },
        { 
          type: "blank", 
          q: "Envías cartas desde la ___ office.", 
          options: ["post"], 
          a: "post" 
        },
        { 
          type: "mc", 
          q: "Edificio del gobierno local (Ayuntamiento):", 
          options: ["Town Hall", "Castle", "Library"], 
          a: "Town Hall" 
        },
        { 
          type: "match", 
          q: "Conecta el servicio:",
          pairs: [
            { left: "Library", right: "📖 Borrow Books" },
            { left: "Bookstore", right: "💲 Buy Books" }, 
            { left: "Post Office", right: "✉️ Send Mail" },
            { left: "Fire Station", right: "🚒 Firefighters" }
          ],
          a: "completed",
          tip: "Cuidado: 'Library' es biblioteca (prestan). 'Bookstore' es librería (venden)."
        },
        { 
          type: "order", 
          q: "Ordena: Hay una biblioteca vieja.", 
          words: ["There", "is", "an", "old", "library."], 
          a: "There is an old library." 
        },
        { 
          type: "input", 
          q: "Escribe 'Policía' en inglés:", 
          a: "police" 
        },
        { 
          type: "mc", 
          q: "Edificio donde se guardan libros:", 
          options: ["Library", "Book shop", "Station"], 
          a: "Library" 
        },
        { 
          type: "order", 
          q: "Ordena: La estación de policía está cerca.", 
          words: ["The", "police", "station", "is", "near."], 
          a: "The police station is near." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 6: CULTURE & HISTORY
    // Objetivo: Lugares de culto y edificios históricos.
    // ----------------------------------------------------------------------
    {
      id: "u20-set6",
      title: "Set 6: Culture & History",
      questions: [
        { 
          type: "match", 
          q: "Lugares de culto:",
          pairs: [
            { left: "Church", right: "✝️ Christian" },
            { left: "Mosque", right: "☪️ Muslim" },
            { left: "Synagogue", right: "✡️ Jewish" },
            { left: "Temple", right: "🕉️ Buddhist" }
          ],
          a: "completed" 
        },
        { 
          type: "mc", 
          q: "Edificio antiguo donde vivían reyes 👑:", 
          options: ["Castle", "Museum", "Town Hall"], 
          a: "Castle" 
        },
        { 
          type: "blank", 
          q: "Un puente cruza el río: ___.", 
          options: ["bridge"], 
          a: "bridge" 
        },
        { 
          type: "order", 
          q: "Ordena: El castillo es muy viejo.", 
          words: ["The", "castle", "is", "very", "old."], 
          a: "The castle is very old." 
        },
        { 
          type: "input", 
          q: "Lugar religioso cristiano (escribe en inglés):", 
          a: "church" 
        },
        { 
          type: "mc", 
          q: "Un edificio muy alto con oficinas:", 
          options: ["Office building", "House", "Cottage"], 
          a: "Office building" 
        },
        { 
          type: "blank", 
          q: "La mezquita está en la calle Main: The ___ is on Main St.", 
          options: ["mosque"], 
          a: "mosque" 
        },
        { 
          type: "order", 
          q: "Ordena: Hay un puente bonito.", 
          words: ["There", "is", "a", "beautiful", "bridge."], 
          a: "There is a beautiful bridge." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 7: GRAMMAR - THERE IS / THERE ARE
    // Objetivo: Describir existencia (Singular vs Plural).
    // ----------------------------------------------------------------------
    {
      id: "u20-set7",
      title: "Set 7: Describing Towns",
      questions: [
        { 
          type: "mc", 
          q: "En mi ciudad ___ un parque. (Singular)", 
          options: ["there is", "there are"], 
          a: "there is" 
        },
        { 
          type: "mc", 
          q: "En mi pueblo ___ dos bancos. (Plural)", 
          options: ["there are", "there is"], 
          a: "there are" 
        },
        { 
          type: "blank", 
          q: "___ (No hay) any museums here.", 
          options: ["There aren't"], 
          a: "There aren't",
          tip: "Para plural negativo usamos 'There aren't' + 'any'."
        },
        { 
          type: "match", 
          q: "Empareja la gramática:",
          pairs: [
            { left: "There is", right: "Singular (1)" },
            { left: "There are", right: "Plural (2+)" },
            { left: "Is there...?", right: "Question (Singular)" },
            { left: "Are there...?", right: "Question (Plural)" }
          ],
          a: "completed"
        },
        { 
          type: "order", 
          q: "Ordena: Hay un hotel bonito.", 
          words: ["There", "is", "a", "beautiful", "hotel."], 
          a: "There is a beautiful hotel." 
        },
        { 
          type: "input", 
          q: "Escribe: 'Hay' (para plural) en inglés:", 
          a: "there are" 
        },
        { 
          type: "mc", 
          q: "___ a pharmacy near here?", 
          options: ["Is there", "Are there"], 
          a: "Is there",
          tip: "Pharmacy es singular, usamos 'Is there'."
        },
        { 
          type: "order", 
          q: "Ordena: No hay cines.", 
          words: ["There", "aren't", "any", "cinemas."], 
          a: "There aren't any cinemas." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 8: GIVING DIRECTIONS (Survival)
    // Objetivo: Dar y entender indicaciones.
    // ----------------------------------------------------------------------
    {
      id: "u20-set8",
      title: "Set 8: Directions",
      questions: [
        { 
          type: "match", 
          q: "Conecta la instrucción:",
          pairs: [
            { left: "Turn left", right: "⬅️ Izquierda" },
            { left: "Turn right", right: "➡️ Derecha" },
            { left: "Go straight", right: "⬆️ Recto" },
            { left: "Go past", right: "Pasa por delante" }
          ],
          a: "completed" 
        },
        { 
          type: "mc", 
          q: "La farmacia está ___ la esquina.", 
          options: ["on", "in", "at"], 
          a: "on",
          tip: "Decimos 'ON the corner'."
        },
        { 
          type: "order", 
          q: "Ordena: Gira a la derecha en el banco.", 
          words: ["Turn", "right", "at", "the", "bank."], 
          a: "Turn right at the bank." 
        },
        { 
          type: "blank", 
          q: "Sigue recto: Go straight ___.", 
          options: ["ahead"], 
          a: "ahead" 
        },
        { 
          type: "mc", 
          q: "Is it far? No, it's ___.", 
          options: ["near", "opposite"], 
          a: "near" 
        },
        { 
          type: "input", 
          q: "Escribe: Izquierda (en inglés):", 
          a: "left" 
        },
        { 
          type: "input", 
          q: "Escribe: Derecha (en inglés):", 
          a: "right" 
        },
        { 
          type: "order", 
          q: "Ordena: Pasa el supermercado.", 
          words: ["Go", "past", "the", "supermarket."], 
          a: "Go past the supermarket." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 9: ERROR CORRECTION (Grammar Police)
    // Objetivo: Corregir errores comunes de preposiciones y gramática.
    // ----------------------------------------------------------------------
    {
      id: "u20-set9",
      title: "Set 9: Find the Mistake",
      questions: [
        { 
          type: "mc", 
          q: "Error: 'The bank is next the park.'", 
          options: ["Fix: next TO the park", "Fix: NEAR the park"], 
          a: "Fix: next TO the park",
          tip: "'Next' siempre va acompañado de 'to'."
        },
        { 
          type: "mc", 
          q: "Error: 'There is two hotels.'", 
          options: ["Fix: There ARE two hotels", "Fix: There IS a hotel"], 
          a: "Fix: There ARE two hotels",
          tip: "Plural (two hotels) requiere 'Are'."
        },
        { 
          type: "blank", 
          q: "Correction: The bar is between the bank. (Falta algo)", 
          options: ["and the store"], 
          a: "and the store",
          tip: "'Between' necesita dos puntos de referencia: A **and** B."
        },
        { 
          type: "order", 
          q: "Corrige: 'Library the is where?'", 
          words: ["Where", "is", "the", "library?"], 
          a: "Where is the library?" 
        },
        { 
          type: "mc", 
          q: "Error: 'I am in the bus stop.' (Preposición)", 
          options: ["Fix: AT the bus stop", "Fix: ON the bus stop"], 
          a: "Fix: AT the bus stop",
          tip: "Para puntos específicos en la ciudad (parada, puerta), usamos 'AT'."
        },
        { 
          type: "input", 
          q: "Corrige: 'Turn in left.' (Quita una palabra)", 
          a: "Turn left",
          tip: "No usamos preposición. Solo verbo + dirección."
        },
        { 
          type: "mc", 
          q: "Error: 'Is there any museums?'", 
          options: ["Fix: Are there any...?", "Fix: Is there a...?"], 
          a: "Fix: Are there any...?",
          tip: "Museums es plural -> Are there."
        },
        { 
          type: "order", 
          q: "Corrige: 'There is many cars.'", 
          words: ["There", "are", "many", "cars."], 
          a: "There are many cars." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 10: MASTER CHALLENGE (City Guide)
    // Objetivo: Demostrar dominio total del módulo.
    // ----------------------------------------------------------------------
    {
      id: "u20-set10",
      title: "Set 10: City Guide Master",
      questions: [
        { 
          type: "mc", 
          q: "Definición: Lugar para ver cosas antiguas.", 
          options: ["Museum", "Library", "Factory"], 
          a: "Museum" 
        },
        { 
          type: "order", 
          q: "Master: Disculpe, ¿dónde está el hospital?", 
          words: ["Excuse", "me,", "where", "is", "the", "hospital?"], 
          a: "Excuse me, where is the hospital?",
          successTip: "¡Muy educado! 'Excuse me' es la forma perfecta de empezar."
        },
        { 
          type: "input", 
          q: "Escribe el opuesto de 'Near':", 
          a: "far" 
        },
        { 
          type: "match", 
          q: "Match US vs UK/Canada:",
          pairs: [
            { left: "Apartment", right: "Flat" },
            { left: "Elevator", right: "Lift" },
            { left: "Parking lot", right: "Car park" },
            { left: "Gas", right: "Petrol" }
          ],
          a: "completed",
          successTip: "¡Experto bilingüe! (Americano y Británico/Canadiense)."
        },
        { 
          type: "blank", 
          q: "The cinema is ___ (entre) the bank and the café.", 
          options: ["between"], 
          a: "between" 
        },
        { 
          type: "mc", 
          q: "¿Cómo se llama el baño en Canadá? (Polite)", 
          options: ["Washroom", "Toilet", "Restroom"], 
          a: "Washroom" 
        },
        { 
          type: "order", 
          q: "Describe: No hay tiendas aquí.", 
          words: ["There", "aren't", "any", "shops", "here."], 
          a: "There aren't any shops here." 
        },
        { 
          type: "input", 
          q: "Escribe 'Aeropuerto' en inglés:", 
          a: "airport" 
        }
      ]
    }
  ]
};